import { useEffect, useState, useCallback, useRef } from 'react';

// Formatery żyją w lib/natsEventMessage (czyste, testowalne bez Reacta);
// re-eksport utrzymuje dotychczasowe importy z tego modułu.
import { czyDlaCzlowieka, formatEventMessage } from '../lib/natsEventMessage';
import { parseNatsSubject, type NatsCategory } from '../lib/natsSubject';
import { useWebSocket, useWsRetryStatus } from '../providers/WebSocketProvider';

export {
  getCategoryLabel,
  getCategoryColor,
  formatAction,
  formatSource,
  formatEventMessage,
  opiszZrodlo,
  czyDlaCzlowieka,
  type KontekstZdarzenia,
} from '../lib/natsEventMessage';

export interface NatsEvent {
  id: string;
  subject: string;
  category: NatsCategory;
  action: string;
  source: string;
  /** Identyfikator urządzenia wyłuskany z subjectu (telemetria, status, bateria, alerty). */
  deviceId?: string;
  /** Sygnał telemetryczny wyłuskany z subjectu. */
  signal?: string;
  tenantId?: string;
  data: unknown;
  timestamp: Date;
  // Human-readable formatted message
  message: string;
  // Optional icon suggestion
  icon?: string;
}

interface UseNatsActivityOptions {
  maxEvents?: number;
  filter?: (event: NatsEvent) => boolean;
}

export function useNatsActivity(options: UseNatsActivityOptions = {}) {
  const { maxEvents = 50, filter } = options;
  const { isConnected, reconnectNow, subscribeRaw } = useWebSocket();
  // Stan ponawiania z osobnego kontekstu — zmienia się co sekundę, więc
  // przerysowuje tylko panel aktywności, a nie każdego konsumenta `useWebSocket`
  // (np. kanwę globu w `LivingNetwork`).
  const { connectionState, reconnectAttempt, retryInSeconds } = useWsRetryStatus();
  const [events, setEvents] = useState<NatsEvent[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const eventIdCounter = useRef(0);
  const isPausedRef = useRef(isPaused);

  // Keep isPausedRef in sync
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // Subscribe to raw NATS events
  useEffect(() => {
    const unsubscribe = subscribeRaw((message) => {
      if (isPausedRef.current) return;

      const { subject, data } = message;
      const parsed = parseNatsSubject(subject);
      // Rytm serca platformy nie jest zdarzeniem najemcy — nie wchodzi do
      // listy ani do 50-elementowego bufora (lib/natsEventMessage.ts).
      if (!czyDlaCzlowieka(parsed)) return;
      const { message: formattedMessage, icon } = formatEventMessage(parsed, data);

      const event: NatsEvent = {
        id: `nats-${Date.now()}-${eventIdCounter.current++}`,
        subject,
        category: parsed.category,
        action: parsed.action,
        source: parsed.source,
        deviceId: parsed.deviceId,
        signal: parsed.signal,
        tenantId: parsed.tenantId,
        data,
        timestamp: new Date(),
        message: formattedMessage,
        icon,
      };

      // Apply filter if provided
      if (filter && !filter(event)) return;

      setEvents((prev) => {
        const newEvents = [event, ...prev];
        return newEvents.slice(0, maxEvents);
      });
    });

    return unsubscribe;
  }, [subscribeRaw, maxEvents, filter]);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  const togglePause = useCallback(() => {
    setIsPaused((prev) => !prev);
  }, []);

  return {
    events,
    isConnected,
    connectionState,
    reconnectAttempt,
    retryInSeconds,
    reconnectNow,
    isPaused,
    clearEvents,
    togglePause,
  };
}
